import { httpClient } from './api/httpClient';
import { tokenService } from './api/tokenService';
import { isMockEnabled, mockResponse, mockError } from './api/mockAdapter';
import { API_ENDPOINTS } from 'constants/apiEndpoints';
import { MESSAGES } from 'constants/messages';
import { MOCK_USERS } from 'mock/users.mock';
import { MOCK_ROLES } from 'mock/roles.mock';

/**
 * Auth service.
 *
 * Real backend contract:
 *   POST /auth/login   -> { access_token, refresh_token, user, permissions }
 *   POST /auth/refresh -> { access_token }            (handled in axios layer)
 *
 * There is no "current user" endpoint, so the user profile (including its
 * `permissions` matrix) captured at login is persisted and restored from
 * storage on reload. `permissions` is the backend's own RBAC grant —
 * `{ [module]: { [action]: boolean } }` — taken as-is, no client-side
 * inference. The System Administrator (`is_system_admin`/`bypass_permissions`)
 * sends no `permissions` at all; see isAdminBypass in utils/permissions.
 */
const toUser = (rawUser) =>
  rawUser && {
    id: rawUser.id,
    fullName: rawUser.full_name ?? rawUser.fullName ?? '',
    phoneNumber: rawUser.phone_number ?? rawUser.phoneNumber ?? '',
    roleId: rawUser.role_id ?? rawUser.roleId ?? null,
    roleName: rawUser.role_name ?? rawUser.roleName ?? '',
    status: rawUser.status,
    // Built-in Super Administrator flags. When set, the backend sends no
    // `sidebar` at all — see isAdminBypass in utils/permissions.
    isSystemAdmin: Boolean(rawUser.is_system_admin ?? rawUser.isSystemAdmin),
    bypassPermissions: Boolean(rawUser.bypass_permissions ?? rawUser.bypassPermissions),
    allowedNewspapers: rawUser.allowed_newspapers ?? rawUser.allowedNewspapers ?? ['ALL'],
  };

/* ---- Mock helpers (preserve the offline demo when REACT_APP_USE_MOCK=true) -- */
const resolveMockUser = (rawUser) => {
  const role = MOCK_ROLES.find((r) => r.id === rawUser.role_id);
  return {
    ...toUser(rawUser),
    allowedNewspapers: role?.allowed_newspapers ?? ['ALL'],
    roleName: role?.role_name ?? '',
    permissions: role?.permissions ?? {},
  };
};

export const authService = {
  async login({ phoneNumber, password }) {
    if (isMockEnabled()) {
      const found = MOCK_USERS.find(
        (u) => u.phone_number === String(phoneNumber).trim() && u.password === password
      );
      if (!found) return mockError(401, MESSAGES.AUTH.LOGIN_FAILED);
      tokenService.setTokens({
        accessToken: `mock-access-${found.id}`,
        refreshToken: `mock-refresh-${found.id}`,
      });
      const user = resolveMockUser(found);
      return mockResponse({ user });
    }

    const data = await httpClient.post(API_ENDPOINTS.AUTH.LOGIN, {
      phone_number: phoneNumber,
      password,
    });

    const user = { ...toUser(data.user), permissions: data.permissions || {} };

    tokenService.setSession({
      accessToken: data.access_token ?? data.accessToken,
      refreshToken: data.refresh_token ?? data.refreshToken,
      user,
    });

    return { user };
  },

  /**
   * Restore the session on app load. Mock mode resolves from the token id; real
   * mode rehydrates the persisted profile (no backend "me" endpoint) — its
   * `permissions` were already captured as-is at login time.
   */
  async restoreSession() {
    if (isMockEnabled()) {
      const token = tokenService.getAccessToken() || '';
      const id = token.replace('mock-access-', '');
      const found = MOCK_USERS.find((u) => u.id === id);
      if (!found) return mockError(401, MESSAGES.GENERIC.SESSION_EXPIRED);
      return mockResponse({ user: resolveMockUser(found) });
    }

    const user = tokenService.getUser();
    if (!user) {
      const err = new Error(MESSAGES.GENERIC.SESSION_EXPIRED);
      err.response = { status: 401, data: { detail: MESSAGES.GENERIC.SESSION_EXPIRED } };
      throw err;
    }
    return { user };
  },

  async logout() {
    // No server-side logout endpoint in this phase; clear the local session.
    tokenService.clearTokens();
    return true;
  },
};

export default authService;
